// useReleaseTemplate — the band's ONE to-do template: the rows every new release is stamped with.
// Built on useBandQuery/useBandMutation → keys ['band', bandId, …].
//
// The route is singular and so is this hook: there is no template id, no picker and no "apply to this
// release" — stamping happens at release creation and nowhere else (.design-specs/release-settings.audit.md).
// Save is a whole-document PUT guarded by If-Match, because a partial save of a list is how two editors
// silently delete each other's rows. Server-confirm then repaint (never a phantom success).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type ReleaseTemplateRead = components["schemas"]["ReleaseTemplateRead"];
type ReleaseTemplateRowDto = components["schemas"]["ReleaseTemplateRowDto"];
type ReleaseTemplateRowInput = components["schemas"]["ReleaseTemplateRowInput"];
type SaveReleaseTemplateRequest = components["schemas"]["SaveReleaseTemplateRequest"];

export type { ReleaseTemplateRead, ReleaseTemplateRowDto, ReleaseTemplateRowInput };

/** The template plus its ETag version — the PUT needs the version, so the read must carry it. */
export interface ReleaseTemplateState {
  rows: ReleaseTemplateRowDto[];
  version: number;
}

const templateKey = (bandId: string) => ["band", bandId, "release-template"] as const;

/** Parse the ETag (`"3"`) the controller stamps on both GET and PUT. */
function etag(response: Response): number {
  const raw = response.headers.get("etag")?.replace(/^W\//, "").replace(/"/g, "").trim();
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** `GET /bands/{bandId}/release-template` — never 404s; a band is born holding one. */
export function useReleaseTemplate() {
  const { api } = useApp();
  return useBandQuery<ReleaseTemplateState>(
    ["release-template"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/release-template", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return { rows: data.rows ?? [], version: etag(response) };
    },
    { staleTime: 5 * 60_000 },
  );
}

/**
 * `PUT /bands/{bandId}/release-template` — the WHOLE template, in order. A row keeps its `rowId` so the
 * server can update it IN PLACE; that is what keeps already-stamped tasks pointing at the row that made
 * them, so a template edit doesn't quietly break their due-date recompute. A 409 means someone else saved
 * while this editor was open — surfaced verbatim rather than retried, since a silent retry would clobber
 * exactly the rows the conflict is warning about.
 */
export function useSaveReleaseTemplate() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseTemplateState, { rows: ReleaseTemplateRowInput[]; version: number }>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/release-template", {
        params: { path: { bandId: band } },
        headers: { "If-Match": `"${body.version}"` },
        body: { rows: body.rows } satisfies SaveReleaseTemplateRequest,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return { rows: data.rows ?? [], version: etag(response) };
    },
    {
      onSuccess: (next) => qc.setQueryData(templateKey(bandId), next),
      onError: (err) =>
        pushToast(
          err instanceof ApiError && err.status === 409
            ? "Someone else saved these settings while you had them open. Reopen to see their version."
            : errorMessage(err, "Couldn't save the template"),
        ),
    },
  );
}

/**
 * `POST /bands/{bandId}/release-template/reset` — throw the template away and put the four product defaults
 * back. Carries `If-Match` like the save does, so it cannot silently undo someone else's edit.
 *
 * Destructive in a way the save is not: it DELETES rows, so every task already stamped from one loses its
 * back-link and stops following the release date. The caller confirms first.
 */
export function useResetReleaseTemplate() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ReleaseTemplateState, { version: number }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/release-template/reset", {
        params: { path: { bandId: band } },
        headers: { "If-Match": `"${body.version}"` },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return { rows: data.rows ?? [], version: etag(response) };
    },
    {
      onSuccess: (next) => qc.setQueryData(templateKey(bandId), next),
      onError: (err) =>
        pushToast(
          err instanceof ApiError && err.status === 409
            ? "Someone else saved these settings while you had them open. Reopen to see their version."
            : errorMessage(err, "Couldn't reset the template"),
        ),
    },
  );
}
